export interface ITranslateElements {
    attr: string | undefined;
    element: Node;
    text: boolean;
}

const excludes = ["html", "head", "style", "title", "link", "meta", "script"],
    // translate attributes (title, aria-title, placeholder) but don't translate nodes inside
    excludeLookInside = ["iframe"],
    whitespace = /^\s*$/,
    // functions to select correct form can be found here:
    // http://i18next.com/docs/plurals/
    // https://developer.mozilla.org/en-US/docs/Mozilla/Localization/Localization_and_Plurals#Developing_with_PluralForm
    deciders: Record<string, (e: number, variants: string[]) => string> = {
        en: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        fr: (e, variants) => {
            return variants[Number(+e >= 2)];
        },
        es: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "es-419": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "es-ES": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        ja: (_, variants) => {
            return variants[0];
        },
        de: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        pl: (n, variants) => {
            let e = +n;
            return variants[
                Number(
                    e % 10 === 1
                        ? 0
                        : e % 10 >= 2 &&
                          4 >= e % 10 &&
                          (10 > e % 100 || e % 100 >= 20)
                        ? 1
                        : 2
                )
            ];
        },
        "pt-BR": (e, variants) => {
            return variants[Number(+e >= 2)];
        },
        nl: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        it: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "zh-Hans": (_, variants) => {
            return variants[0];
        },
        th: (_, variants) => {
            return variants[0];
        },
        id: (_, variants) => {
            return variants[0];
        },
        cs: (n, variants) => {
            let e = +n;
            return variants[Number(1 === e ? 0 : e >= 2 && e <= 4 ? 1 : 2)];
        },
        sv: (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        tr: (e, variants) => {
            return variants[Number(+e >= 2)];
        },
        ms: (_, variants) => {
            return variants[0];
        },
        "fr-FR": (e, variants) => {
            return variants[Number(+e >= 2)];
        },
        "ko-KR": (_, variants) => {
            return variants[0];
        },
        "zh-TW": (_, variants) => {
            return variants[0];
        },
        "da-DK": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "fi-FI": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "hu-HU": (_, variants) => {
            return variants[0];
        },
        "nb-NO": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "sk-SK": (n, variants) => {
            let e = +n;
            return variants[Number(1 === e ? 0 : e >= 2 && e <= 4 ? 1 : 2)];
        },
        "ro-RO": (n, variants) => {
            let e = +n;
            return variants[
                Number(
                    e === 1
                        ? 0
                        : (e === 0 || (e % 100 > 0 && e % 100 < 20))
                        ? 1
                        : 2
                )
                ];
        },
        "pt-PT": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "bg-BG": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "el-GR": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "et-EE": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "ka-GE": (e, variants) => {
            return variants[Number(1 !== +e)];
        },
        "lt-LT": (n, variants) => {
            let e = +n;
            return variants[
                Number(
                    e % 10 === 1 && e % 100 !== 11
                        ? 0
                        : e % 10 >= 2 && (e % 100 < 10 || e % 100 >= 20)
                        ? 1
                        : 2
                )
            ];
        },
        "lv-LV": (n, variants) => {
            let e = +n;
            return variants[
                Number(e % 10 === 1 && e % 100 !== 11 ? 0 : e !== 0 ? 1 : 2)
            ];
        },
        "ar-001": (n, variants) => {
            let e = +n;
            return variants[
                Number(
                    e === 0
                        ? 0
                        : e === 1
                        ? 1
                        : e === 2
                        ? 2
                        : e % 100 >= 3 && e % 100 <= 10
                        ? 3
                        : e % 100 >= 11 && e % 100 <= 99
                        ? 4
                        : 5
                )
            ];
        },
    };

/**
 * Gets the nodes for translation
 */
export function getNodesForTranslation(
    searchNodes: HTMLElement[]
): ITranslateElements[] {
    let groups: ITranslateElements[] = [],
        attributesToTranslate = [
            "placeholder",
            "title",
            "aria-label",
            "data-tooltip",
        ],
        getTextNodes = (searchNode: Node, checkCurrentNode = false) => {
            let shouldTranslateThis = (elOrClass: Node) => {
                    if (
                        elOrClass === undefined ||
                        elOrClass === null ||
                        !(elOrClass instanceof Element)
                    ) {
                        return false;
                    }
                    let customData = (elOrClass as HTMLElement).dataset || {};
                    let hasAriaTranslated =
                        customData.ariatranslated === "true";
                    return (
                        !hasAriaTranslated &&
                        !elOrClass.classList.contains("noTranslate")
                    );
                },
                checkForAttributes = (el: Element) => {
                    let attrs: string[] = [];
                    for (let i = 0; i < attributesToTranslate.length; i++) {
                        if (el.getAttribute(attributesToTranslate[i])) {
                            attrs.push(attributesToTranslate[i]);
                        }
                    }
                    return attrs;
                },
                getGroupsToTranslate = (node: Node, lookInside: boolean) => {
                    if (
                        node.nodeType === Node.ELEMENT_NODE &&
                        shouldTranslateThis(node) &&
                        excludes.indexOf(node.nodeName.toLowerCase()) === -1
                    ) {
                        checkForAttributes(node as Element).forEach((attr) => {
                            groups.push({
                                text: false,
                                attr,
                                element: node,
                            });
                        });
                        if (
                            lookInside &&
                            excludeLookInside.indexOf(
                                node.nodeName.toLowerCase()
                            ) === -1
                        ) {
                            getTextNodes(node);
                        }
                    }
                    if (
                        node.nodeType === Node.TEXT_NODE &&
                        !whitespace.test((node as Text).data)
                    ) {
                        groups.push({
                            text: true,
                            element: node,
                            attr: undefined,
                        });
                    }
                };

            checkCurrentNode && getGroupsToTranslate(searchNode, false);

            if (shouldTranslateThis(searchNode)) {
                const childNodes = searchNode.childNodes as NodeListOf<Element>;
                let cnLength = childNodes.length;
                while (cnLength--) {
                    getGroupsToTranslate(childNodes[cnLength], true);
                }
            }
        };

    searchNodes.forEach((searchNode: any) => {
        if (searchNode) {
            getTextNodes(searchNode[0] || searchNode, true);
        }
    });

    return groups;
}

export function pluralize(
    language: string,
    sentence: string,
    ...values: number[]
): string {
    let decider = deciders[language],
        valueIndex = 0;

    if (!decider) {
        throw new Error(
            "Pluralization function for " + language + " is not specified"
        );
    }

    return sentence.replace(/\{[^\}]*:?[^\}:]*\}/g, (matches) => {
        let match = matches.substring(1, matches.length - 1),
            parts = match.split(":"),
            pattern = parts[0],
            formsStr = parts[1],
            value = values[valueIndex++];

        if (values.length < valueIndex) {
            throw new Error(
                "Invalid amount of arguments: at least " +
                    valueIndex +
                    " expected but only " +
                    values.length +
                    " given"
            );
        }

        if (parts.length > 2) {
            let indexOf = match.indexOf(":"),
                nextIndexOf = match.indexOf(":", indexOf + 1);
            while (nextIndexOf > -1) {
                indexOf = nextIndexOf;
                nextIndexOf = match.indexOf(":", indexOf + 1);
            }
            parts = [
                match.substring(0, indexOf),
                match.substring(indexOf + 1, match.length),
            ];
        }

        if (parts.length === 2) {
            pattern = parts[0];
            formsStr = parts[1];
        } else {
            pattern = "num form";
            formsStr = parts[0];
        }

        let forms = formsStr.split("|");
        return pattern
            .replace("num", value.toString())
            .replace("form", decider(value, forms) || "");
    });
}

export function pluralizeHtml(lang: string, elements: HTMLElement[]): void {
    elements.forEach((searchNode) => {
        if (searchNode && searchNode.querySelectorAll) {
            let pluralElements = searchNode.querySelectorAll("*[data-plural]");
            Array.prototype.forEach.call(
                pluralElements,
                function (element: HTMLElement) {
                    let text = element.textContent || "",
                        stringValue = element.getAttribute("data-plural") || "";

                    let value = parseInt(stringValue);
                    if (isNaN(value)) {
                        try {
                            value = JSON.parse(stringValue);
                        } catch (e) {
                            value = 0;
                        }
                    }

                    element.textContent = pluralize.apply(
                        undefined,
                        Array.isArray(value)
                            ? [lang, text, ...(value as number[])]
                            : [lang, text, value]
                    );
                }
            );
        }
    });
}

const isTextNode = (node: Node): node is Text => {
    // @ts-ignore
    return node["splitText"] !== undefined;
};

export function replaceOldText(
    groupsForTranslation: ITranslateElements[],
    sentences: string[]
): void {
    const len = sentences.length;
    for (let i = 0; i < len; i++) {
        let part = groupsForTranslation[i];
        const translatedStr = decodeSymbols(sentences[i]);
        if (part.text) {
            const prop = isTextNode(part.element) ? "nodeValue" : "textContent";
            try {
                //IE11 throw sometimes errors when you try to fill text element with text
                part.element[prop] = translatedStr || "";
            } catch (e) {}
        } else if (part.attr !== undefined) {
            (part.element as Element).setAttribute(part.attr, translatedStr);
        }
    }
}

// delete extra spaces, all tabs and line breaks
const cleanUpText = (text: string) => text.replace(/\s{2,}/g, " ");

export function getSentences(
    groupsForTranslation: ITranslateElements[]
): string[] {
    return groupsForTranslation.map(getSentenceText);
}

export const getSentenceText = (val: ITranslateElements) => {
    if (val.text) {
        return cleanUpText(val.element.textContent || "");
    }
    if (val.attr) {
        return (val.element as Element).getAttribute(val.attr) || "";
    }
    return "";
}

const quote = /(&quot;)|(\\u0022)/g,
    cell = /(&#035;)|(&#35;)/g,
    oneQuote = /(&#039;)|(&#39;)|(&apos;)/g,
    andSign = /(&amp;)|(\\u0026)/g;
export function decodeSymbols(str: string): string {
    return str.replace(quote, '"').replace(cell, "#").replace(oneQuote, "'").replace(andSign, "&");
}

